import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { interval, Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { EventUtil, InputAppErrorDisplay } from '@monorepo/utils';
import { TAppIdentifier } from '@monorepo/types';
import { TInputAppState } from '../reducer';
import { ClearAllStorageAndReload } from './reset-app-cache-db.actions';
import { InputAppClientDBFacade } from '../input-app-client-db/input-app-client-db.facade';
import { AppMasterDBFacade } from '../app-master-db/app-master-db.facade';

const SYNC_POLL_INTERVAL_MS = 1000;
const SYNC_POLL_TIMEOUT_MS = 120000;

const CLIENT_DB_APPS: TAppIdentifier[] = [
  'roaming_qc_v2',
  'bundle_qc_v2',
  'endline_qc_v2',
  'md_point_v2',
  'md_point_per_piece_qc_v2',
  'per_piece_qc_v2',
  'per_piece_line_in_v2',
  'line_in_v2',
  'per_piece_washing_in_v2',
  'per_piece_washing_out_v2',
  'cutbank_in_v2',
  'cutbank_out_v2',
  'embellishment_in_v2',
  'embellishment_out_v2',
  'assembly_in',
  'component_line_in',
  'aql_v2',
  'cpi',
];

@Injectable({
  providedIn: 'root',
})
export class ResetAppCacheStoreFacade {
  constructor(
    private store: Store<TInputAppState>,
    private alertCtrl: AlertController,
    private translate: TranslateService,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private appMasterDBFacade: AppMasterDBFacade,
    private inputAppErrorUtil: InputAppErrorDisplay,
  ) {}

  async resetAppCacheAfterSyncing() {
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('reset_app_cache.reset_app_cache_confirm_title'),
      message: this.translate.instant('reset_app_cache.reset_app_cache_confirm_message'),
      buttons: [
        {
          text: this.translate.instant('common.cancel'),
          role: 'cancel',
        },
        {
          text: this.translate.instant('common_ui.ok'),
          handler: () => {
            this.startSyncAndReset();
          },
        },
      ],
      cssClass: 'app-update-error',
      backdropDismiss: false,
    });
    alert.present();
  }

  private startSyncAndReset() {
    this.appMasterDBFacade
      .getAppIdentifier()
      .pipe(take(1))
      .subscribe((appIdentifier) => {
        if (!appIdentifier || !CLIENT_DB_APPS.includes(appIdentifier)) {
          this.dispatchClear();
          return;
        }
        this.inputAppClientDBFacade
          .getClientDBAndOnlineStatusAndEventsBatchCountAndCurrentLineAndShift()
          .pipe(take(1))
          .subscribe(({ isOnline, userDB, bgDB }) => {
            const hasPending = EventUtil.hasAnyPendingEvents(userDB, bgDB);
            if (!hasPending) {
              this.dispatchClear();
            } else if (!isOnline) {
              this.inputAppErrorUtil.showPopup(
                this.translate.instant('remote_deployment.sync_failed'),
                this.translate.instant('remote_deployment.check_internet'),
              );
            } else {
              this.syncThenReset();
            }
          });
      });
  }

  private async syncThenReset() {
    await this.inputAppErrorUtil.presentLoadingWithId(
      'SYNCING_BEFORE_RESET',
      this.translate.instant('reset_app_cache.syncing_before_reset'),
    );
    this.inputAppClientDBFacade.backgroundSyncEvents(false);

    const stop$ = new Subject<void>();
    const startTime = Date.now();

    interval(SYNC_POLL_INTERVAL_MS)
      .pipe(
        takeUntil(stop$),
        switchMap(() => this.inputAppClientDBFacade.getClientDBAndBgDB().pipe(take(1))),
      )
      .subscribe(({ userDB, bgDB }) => {
        const stillPending = EventUtil.hasAnyPendingEvents(userDB, bgDB);
        const timedOut = Date.now() - startTime > SYNC_POLL_TIMEOUT_MS;
        if (!stillPending) {
          stop$.next();
          stop$.complete();
          this.inputAppErrorUtil.dismissLoadingWithId('SYNCING_BEFORE_RESET').then(() => {
            this.dispatchClear();
          });
        } else if (timedOut) {
          stop$.next();
          stop$.complete();
          this.inputAppErrorUtil.dismissLoadingWithId('SYNCING_BEFORE_RESET').then(() => {
            this.inputAppErrorUtil.showPopup(
              this.translate.instant('remote_deployment.sync_failed'),
              this.translate.instant('remote_deployment.sync_issue_retry'),
            );
          });
        }
      });
  }

  dispatchClear() {
    this.store.dispatch(new ClearAllStorageAndReload());
  }
}
